import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import SearchAutoComplete from "./SearchAutoComplete";
import ThemeToggle from "./ThemeToggle";
import { LuUser, LuShoppingCart, LuMenu, LuX, LuPhone, LuShield } from "react-icons/lu";

const NAV_LINKS = [
  { path: "/",                          label: "Home",    exact: true },
  { path: "/products/disposable-vapes", label: "Vapes" },
  { path: "/products/kratom",           label: "Kratom" },
  { path: "/products/novelty",          label: "Novelty" },
  { path: "/products/glass",            label: "Glass" },
  { path: "/products/delta",            label: "Delta" },
  { path: "/support",                   label: "Support" },
];

function Navbar() {
  const { user, logout, loading } = useAuth();
  const { cart, getTotalPrice, clearCart } = useCart();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [isAdmin, setIsAdmin]   = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenu = () => setMenuOpen(false);
  const isActive  = (path, exact) => exact ? pathname === path : pathname.startsWith(path);

  useEffect(() => { closeMenu(); }, [pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const handleScroll = () => closeMenu();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [menuOpen]);

  useEffect(() => {
    if (!user) { setIsAdmin(false); return; }
    user.getIdTokenResult()
      .then(result => setIsAdmin(result.claims.admin === true))
      .catch(() => setIsAdmin(false));
  }, [user]);

  if (loading) return null;

  const cartCount = cart.reduce((t, i) => t + i.quantity, 0);

  return (
    <header className="w-full sticky top-0 z-50">

      {/* Enterprise info strip — dark, trust-first */}
      <div className="bg-brand-navy border-b border-white/5 text-slate-400 text-xs flex justify-between items-center px-6 py-1.5">
        <span className="flex items-center gap-1.5">
          <LuPhone size={11} className="text-brand-amber" />
          Support: <strong className="text-white font-semibold ml-0.5">(214) 635-6340</strong>
        </span>
        <span className="hidden sm:block">
          Free shipping on orders{" "}
          <strong className="text-brand-amber font-bold">$500+</strong>
        </span>
        <span className="hidden md:block text-slate-500">
          DFW Local Delivery · 24–48 hrs
        </span>
      </div>

      {/* Main nav — glassmorphic white / dark */}
      <nav className="bg-white/96 dark:bg-[#0E1223]/96 backdrop-blur-md border-b border-slate-200/80 dark:border-border shadow-xs">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-6">

          {/* Logo — icon mark + Lexend wordmark */}
          <Link to="/" onClick={closeMenu} className="flex-shrink-0 flex items-center gap-2.5">
            <div className="bg-white rounded-lg p-1 flex-shrink-0">
              <img
                src="/images/croppedLogo.png"
                alt="Khoja Distributions"
                className="h-9 w-auto"
              />
            </div>
            {/* Wordmark text — hides on very small screens */}
            <div className="hidden sm:flex flex-col leading-none">
              <span className="text-brand-navy font-black text-[16px] tracking-tight" style={{ fontFamily: "Lexend, sans-serif" }}>
                Khoja
              </span>
              <span className="text-brand-amber font-bold text-[9px] tracking-[0.2em] uppercase mt-0.5" style={{ fontFamily: "Lexend, sans-serif" }}>
                Distributions
              </span>
            </div>
          </Link>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-0.5 flex-1">
            {NAV_LINKS.map(({ path, label, exact }) => (
              <Link
                key={path}
                to={path}
                onClick={closeMenu}
                className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${
                  isActive(path, exact)
                    ? "text-brand-amber bg-blue-50 dark:bg-accent/10"
                    : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/5"
                }`}
              >
                {label}
              </Link>
            ))}
            {isAdmin && (
              <Link
                to="/admin"
                onClick={closeMenu}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-bold border transition-all ${
                  isActive("/admin")
                    ? "bg-brand-amber/20 border-brand-amber/50 text-brand-amber"
                    : "bg-brand-amber/10 border-brand-amber/25 text-brand-amber hover:bg-brand-amber/20 hover:border-brand-amber/40"
                }`}
              >
                <LuShield size={13} />
                Admin
              </Link>
            )}
          </div>

          {/* Search — desktop */}
          <div className="hidden lg:block">
            <SearchAutoComplete onSelect={closeMenu} />
          </div>

          {/* Right icons */}
          <div className="hidden md:flex items-center gap-3 ml-auto lg:ml-0">
            <ThemeToggle />

            {user ? (
              <>
                <Link
                  to="/dashboard"
                  onClick={closeMenu}
                  className="text-slate-500 dark:text-slate-400 hover:text-brand-amber dark:hover:text-accent transition-colors cursor-pointer"
                  title="My Account"
                >
                  <LuUser size={20} />
                </Link>
                <button
                  onClick={async () => {
                    clearCart();
                    await logout();
                    setIsAdmin(false);
                    closeMenu();
                    navigate("/", { replace: true });
                  }}
                  className="text-xs text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 transition-colors cursor-pointer"
                >
                  Logout
                </button>
              </>
            ) : (
              <Link
                to="/account"
                onClick={closeMenu}
                className="text-sm font-semibold text-slate-600 dark:text-slate-300 hover:text-brand-amber dark:hover:text-accent transition-colors"
              >
                Login
              </Link>
            )}

            <Link
              to="/cart"
              onClick={closeMenu}
              className="relative flex items-center gap-1.5 bg-brand-amber hover:brightness-110 transition-all text-white text-sm font-bold px-4 py-1.5 rounded-full shadow-blue/30 shadow-md cursor-pointer"
            >
              <LuShoppingCart size={16} />
              <span>${getTotalPrice().toFixed(2)}</span>
              {cartCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-white text-brand-navy text-[10px] font-black rounded-full min-w-[16px] h-4 flex items-center justify-center px-1 shadow-xs">
                  {cartCount}
                </span>
              )}
            </Link>
          </div>

          {/* Mobile right — theme toggle + hamburger */}
          <div className="md:hidden ml-auto flex items-center gap-2">
            <ThemeToggle />
            <button
              className="text-slate-600 dark:text-slate-300 focus:outline-none cursor-pointer"
              onClick={() => setMenuOpen(v => !v)}
              aria-label="Toggle menu"
            >
              {menuOpen ? <LuX size={24} /> : <LuMenu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile search */}
        <div className="lg:hidden px-6 pb-3">
          <SearchAutoComplete onSelect={closeMenu} />
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden border-t border-border px-6 py-4 flex flex-col gap-3 bg-card">
            {NAV_LINKS.map(({ path, label, exact }) => (
              <Link
                key={path}
                to={path}
                onClick={closeMenu}
                className={`text-sm font-semibold py-1.5 transition-colors ${
                  isActive(path, exact)
                    ? "text-brand-amber"
                    : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
                }`}
              >
                {label}
              </Link>
            ))}
            {isAdmin && (
              <Link
                to="/admin"
                onClick={closeMenu}
                className={`flex items-center gap-2 w-fit px-3 py-1 rounded-full text-sm font-bold border transition-all ${
                  isActive("/admin")
                    ? "bg-brand-amber/20 border-brand-amber/50 text-brand-amber"
                    : "bg-brand-amber/10 border-brand-amber/25 text-brand-amber hover:bg-brand-amber/20 hover:border-brand-amber/40"
                }`}
              >
                <LuShield size={13} />
                Admin
              </Link>
            )}
            <div className="flex items-center gap-4 pt-3 border-t border-border">
              {user ? (
                <>
                  <Link
                    to="/dashboard"
                    onClick={closeMenu}
                    className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-brand-amber dark:hover:text-accent"
                  >
                    My Account
                  </Link>
                  <button
                    onClick={async () => {
                      clearCart();
                      await logout();
                      setIsAdmin(false);
                      closeMenu();
                      navigate("/", { replace: true });
                    }}
                    className="text-sm text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 cursor-pointer"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/account"
                  onClick={closeMenu}
                  className="text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-brand-amber dark:hover:text-accent"
                >
                  Login / Register
                </Link>
              )}
              <Link
                to="/cart"
                onClick={closeMenu}
                className="ml-auto flex items-center gap-1.5 bg-brand-amber text-white text-sm font-bold px-4 py-1.5 rounded-full cursor-pointer"
              >
                <LuShoppingCart size={14} />
                Cart · ${getTotalPrice().toFixed(2)}
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}

export default Navbar;

